import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';
import { Button } from '@/components/ui/button';
import { RefreshCw, FileDown, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

const fmt = (n) => `RM ${Number(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}`;
const monthLabel = (lwd) => (lwd ? new Date(`${lwd.slice(0, 7)}-01T00:00:00`).toLocaleDateString('en-MY', { month: 'short', year: 'numeric' }) : '');

const finalMonthLabel = (fm, lwd) => {
  const days = fm.eligible_days != null ? ` · ${fm.eligible_days}/${fm.days_in_month} days` : '';
  if (fm.source === 'estimate') return `Final month net pay — ${monthLabel(lwd)} (projected, payroll not run yet${days})`;
  return `Final month net pay — ${monthLabel(lwd)} payslip (${fm.finalized ? 'finalized' : 'draft'}${fm.prorated ? `, prorated${days}` : ', full month'})`;
};

// Line items of the final-month payslip (same engine as Process Payroll).
function FinalMonthDetails({ fm }) {
  const earn = [
    ['Basic salary', fm.basic], ['Allowances', fm.allowances], ['Overtime', fm.overtime], ['Bonus / adjustments', fm.bonus],
    [`Annual leave encashment${fm.leave_encashment_days ? ` (${Number(fm.leave_encashment_days)} days)` : ''}`, fm.leave_encashment],
    ['Claims reimbursement', fm.claims_reimbursement], ['Less: unpaid leave', -Number(fm.unpaid_leave_deduction || 0)],
  ].filter(([, v]) => Number(v || 0) !== 0);
  const ded = [
    ['EPF (employee)', fm.epf], ['SOCSO (employee)', fm.socso], ['EIS (employee)', fm.eis], ['PCB / MTD', fm.pcb],
    ['SKBKK', fm.skbkk], ['Loan instalment', fm.loan_deduction], ['Other deductions', fm.other_deductions],
  ].filter(([, v]) => Number(v || 0) !== 0);
  const Col = ({ title, items, tone }) => (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">{title}</p>
      {items.length === 0 ? <p className="text-xs text-slate-400">—</p> : items.map(([l, v]) => (
        <div key={l} className="flex justify-between gap-3 text-xs">
          <span className="text-slate-600 dark:text-slate-400">{l}</span>
          <span className={`tabular-nums ${tone}`}>{v < 0 ? `− ${fmt(Math.abs(v))}` : fmt(v)}</span>
        </div>
      ))}
    </div>
  );
  return (
    <div className="mt-2 rounded-md bg-slate-50 dark:bg-slate-800/60 p-3 grid sm:grid-cols-2 gap-3" data-testid="fs-final-month-details">
      <Col title="Earnings" items={earn} tone="text-slate-900 dark:text-slate-100" />
      <Col title="Deductions" items={ded} tone="text-rose-700 dark:text-rose-400" />
      <div className="sm:col-span-2 flex justify-between gap-3 text-xs font-semibold border-t border-slate-200 dark:border-slate-700 pt-1">
        <span>Gross {fmt(fm.gross)} − Deductions {fmt(fm.total_deductions)}{Number(fm.claims_reimbursement || 0) ? ` + Claims ${fmt(fm.claims_reimbursement)}` : ''}</span>
        <span className="tabular-nums" data-testid="fs-final-month-net">= Net {fmt(fm.net)}</span>
      </div>
    </div>
  );
}

// Final settlement breakdown lines. `breakdown` = termination_details.final_settlement_breakdown (or live estimate).
export function FinalSettlementBreakdown({ breakdown, compact = false, onSyncPayslip, syncing = false }) {
  const [showDetails, setShowDetails] = useState(false);
  if (!breakdown) return null;
  const fm = breakdown.final_month || {};
  const le = breakdown.leave_encashment || {};
  const uc = breakdown.unpaid_claims || {};
  const rows = [
    { label: finalMonthLabel(fm, breakdown.last_working_day), value: fm.net, id: 'final-month' },
    le.included_in_payslip
      ? { label: `Annual leave encashment (${Number(le.days || 0)} days × ${fmt(le.daily_rate)}) — paid in the final-month payslip above`, value: le.amount, id: 'leave-encashment', included: true }
      : { label: `Annual leave encashment (${Number(le.days || 0)} days × ${fmt(le.daily_rate)})`, value: le.amount, id: 'leave-encashment' },
    { label: `Approved claims not yet paid${uc.claims?.length ? ` (${uc.claims.length})` : ''}`, value: uc.amount, id: 'unpaid-claims' },
    { label: 'Less: outstanding staff loan', value: -Number(breakdown.outstanding_loan || 0), id: 'loan', negative: true },
  ];
  return (
    <div className={`space-y-1 ${compact ? 'text-xs' : 'text-sm'}`} data-testid="final-settlement-breakdown">
      {fm.needs_reproration && (
        <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-2 text-xs text-amber-800 dark:text-amber-300" data-testid="fs-reproration-warning">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div className="flex-1">
            {fm.sync_issue === 'leave_encashment_missing'
              ? 'This payslip was generated before annual leave encashment became part of the final-month payslip — re-run it so the encashment appears as a payslip line.'
              : <>This payslip was processed before the last working day was recorded — it is {fm.prorated ? 'prorated differently' : 'full-month pay'} (expected {fm.expected_eligible_days}/{fm.days_in_month} days).</>}
            {fm.finalized ? ' It is finalized: unlock it in Payroll and click Recalculate.' : ''}
          </div>
          {onSyncPayslip && !fm.finalized && (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onSyncPayslip} disabled={syncing} data-testid="fs-reprorate-btn">
              {syncing ? 'Updating…' : fm.sync_issue === 'leave_encashment_missing' ? 'Re-run final payslip' : 'Re-prorate payslip'}
            </Button>
          )}
        </div>
      )}
      {rows.map((r) => (
        <div key={r.id} className="flex justify-between gap-3">
          <span className="text-slate-600 dark:text-slate-400">
            {r.label}
            {r.id === 'final-month' && fm.gross != null && (
              <button type="button" onClick={() => setShowDetails((s) => !s)} className="ml-2 inline-flex items-center text-xs text-violet-700 dark:text-violet-300 hover:underline" data-testid="fs-toggle-final-month-details">
                {showDetails ? <>hide <ChevronUp className="h-3 w-3" /></> : <>details <ChevronDown className="h-3 w-3" /></>}
              </button>
            )}
          </span>
          <span className={`tabular-nums font-medium ${r.included ? 'text-slate-400 dark:text-slate-500 italic' : r.negative && Number(r.value) !== 0 ? 'text-rose-700 dark:text-rose-400' : 'text-slate-900 dark:text-slate-100'}`} data-testid={`fs-${r.id}`}>
            {r.included ? `incl. ${fmt(r.value)}` : r.negative ? `− ${fmt(Math.abs(r.value))}` : fmt(r.value)}
          </span>
        </div>
      ))}
      {showDetails && <FinalMonthDetails fm={fm} />}
      <div className="flex justify-between gap-3 pt-1 border-t border-slate-200 dark:border-slate-700 font-semibold">
        <span>{breakdown.manual_amount ? 'Computed total (reference)' : 'Total Final Settlement'}</span>
        <span className={`tabular-nums ${breakdown.manual_amount ? 'text-slate-500 dark:text-slate-400 line-through decoration-slate-400' : 'text-emerald-700 dark:text-emerald-400'}`} data-testid={breakdown.manual_amount ? 'fs-computed-total' : 'fs-total'}>
          {fmt(breakdown.computed_total ?? breakdown.total)}
        </span>
      </div>
      {breakdown.manual_amount ? (
        <div className="flex justify-between gap-3 font-semibold">
          <span>Final Settlement Payable <span className="text-xs font-normal text-amber-700 dark:text-amber-400">(adjusted by HR)</span></span>
          <span className="tabular-nums text-emerald-700 dark:text-emerald-400" data-testid="fs-total">{fmt(breakdown.manual_amount)}</span>
        </div>
      ) : null}
    </div>
  );
}

export function FinalSettlementPanel({ employee, canManage, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [live, setLive] = useState(null);
  const td = employee?.termination_details || {};
  const stored = td.final_settlement_breakdown;

  // Always show the current payroll-engine figures; the stored copy may predate payslip changes.
  useEffect(() => {
    if (!employee?.employee_id || !td.last_working_day || !canManage) return;
    let cancelled = false;
    axios.get(`${API}/employees/${employee.employee_id}/final-settlement`)
      .then((res) => { if (!cancelled) setLive(res.data); })
      .catch(() => { if (!cancelled) setLive(null); });
    return () => { cancelled = true; };
  }, [employee?.employee_id, td.last_working_day, td.final_settlement_amount, canManage]);

  const breakdown = live || stored;
  const stale = Boolean(live && stored && Math.abs(Number(live.total) - Number(stored.computed_total ?? stored.total)) >= 0.01);

  const recalc = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/employees/${employee.employee_id}/final-settlement/recalculate`);
      toast.success('Final settlement recalculated');
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to recalculate');
    } finally {
      setBusy(false);
    }
  };

  const syncPayslip = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/employees/${employee.employee_id}/final-settlement/sync-payslip`);
      const s = res.data?.payslip_sync || {};
      if (s.status === 'updated') toast.success(`Final-month payslip re-run — ${s.expected_eligible_days}/${s.days_in_month} days, net ${fmt(s.net_salary)}`);
      else toast.info(s.message || `Payslip status: ${s.status}`);
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update payslip');
    } finally {
      setSyncing(false);
    }
  };

  const downloadLetter = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/employees/${employee.employee_id}/final-settlement/letter`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Final_Settlement_${(employee.full_name || employee.employee_id).replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
      toast.success('Settlement letter downloaded');
      onUpdated?.();
    } catch (err) {
      let detail = 'Failed to generate settlement letter';
      try { detail = JSON.parse(await err.response?.data?.text())?.detail || detail; } catch (_) { /* blob without JSON */ }
      toast.error(detail);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="col-span-1 lg:col-span-2 rounded-lg bg-white/70 dark:bg-slate-900/40 p-4 border border-slate-200 dark:border-slate-700" data-testid="final-settlement-panel">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div>
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">Final Settlement {td.final_settlement_manual_amount ? <span className="text-xs font-normal text-amber-700">(manual amount entered by HR)</span> : null}</p>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">Final-month pay uses the same payroll engine as Process Payroll, prorated to the last working day.</p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={recalc} disabled={busy} data-testid="recalc-final-settlement-btn" className="h-8">
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? 'animate-spin' : ''}`} /> Recalculate
            </Button>
            <Button size="sm" onClick={downloadLetter} disabled={downloading || !td.last_working_day} data-testid="download-settlement-letter-btn" className="h-8 bg-slate-900 hover:bg-slate-800 dark:bg-emerald-600 dark:hover:bg-emerald-700 text-white">
              <FileDown className="h-3.5 w-3.5 mr-1" /> {downloading ? 'Preparing…' : 'Settlement Letter (PDF)'}
            </Button>
          </div>
        )}
      </div>
      {stale && (
        <p className="text-xs text-amber-700 dark:text-amber-400 mb-2" data-testid="fs-stale-note">
          Payroll figures changed since this settlement was saved (saved {fmt(stored.computed_total ?? stored.total)} → now {fmt(live.total)}). Click Recalculate to save the updated total.
        </p>
      )}
      {breakdown ? (
        <FinalSettlementBreakdown
          breakdown={{ ...breakdown, manual_amount: breakdown.manual_amount ?? td.final_settlement_manual_amount ?? null, computed_total: breakdown.computed_total ?? breakdown.total }}
          onSyncPayslip={canManage ? syncPayslip : undefined}
          syncing={syncing}
        />
      ) : (
        <p className="text-sm text-slate-500" data-testid="fs-empty">{fmt(td.final_settlement_amount)} — click Recalculate to compute the breakdown (prorated final pay + leave encashment + unpaid claims − loan).</p>
      )}
    </div>
  );
}
